<script lang="ts">
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/components/Chart/chart_config';

	import { status_configurator, checkIsCriticalEvent } from 'src/helpers/device_data_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	import { LabelListV2, ParameterCardV2 } from '@Shelly/shelly-design-system';

	import ThumbDeviceChartV2 from 'src/components/Chart/ThumbDeviceChartV2.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';

	import type { Device } from 'src/types/Device.ts';

	/**
	 * Main control tab for the S4SN-0A61X (Gen4 presence sensor) sidepanel drawer.
	 *
	 * Responsibilities:
	 * - displays label list with sensor statuses (motion, illuminance, battery)
	 * - shows device charts when configured
	 * - shows sensor status as ParameterCard
	 * - shows firmware incompatibility warnings
	 *
	 * @param {Device} device - The device for which the information is displayed.
	 */
	export let device: Device;

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	$: deviceConfig = devices_config?.[device?.type];

	let timerLabels: Record<string, any> = {};

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	$: statuses =
		status?.online && deviceConfig ? (status_configurator(status, device, settings) ?? []) : [];

	$: labelList = Array.isArray(statuses)
		? statuses.filter(
				(entry) =>
					typeof entry.main === 'undefined' &&
					entry.name !== 'state' &&
					typeof entry.peripheralType === 'undefined',
			)
		: [];

	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	$: mainLabel = Array.isArray(statuses)
		? statuses.find((e) => typeof e.main !== 'undefined' || e.name === 'state')
		: undefined;

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	const handleOnClickThumbChart = () => {
		navigateToHistoryStatisticsTab();
	};
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if status?.online && combinedLabelList.length}
			<div class="lable-list-wrapper-in-drawer-tab-content mb-s">
				<LabelListV2
					backgroundColor="--color-1"
					labelList={combinedLabelList}
					size="m"
					data-prevent-drawer-swipe
					data-prevent-sidepanel-swipe
					isDefaultCursor
					a11yProps={{ isInteractive: true }}
					on:label-click={(e) => {
						e.stopPropagation();
					}}
				/>
			</div>
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div slot="content" class="flex-1"></div>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		{#if deviceChartsConfig.length}
			<ThumbDeviceChartV2
				class={mainLabel ? 'mb-s' : ''}
				{device}
				{deviceChartsConfig}
				on:click={handleOnClickThumbChart}
			/>
		{/if}
		{#if mainLabel}
			{@const isCriticalEvent = checkIsCriticalEvent(mainLabel.value)}
			<ParameterCardV2
				class="mb-s"
				icon={mapIconToThin(mainLabel.icon)}
				title={capFirstChar(mainLabel.value)}
				description={capFirstChar(mainLabel.translation)}
				backgroundColor={isCriticalEvent ? '--color-danger-10' : '--color-0'}
			/>
		{/if}
	</div>
</DeviceViewStateWrapper>
